import { brandPalette } from "../../../constants/brandPalette";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Text,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { RiCheckFill, RiFileEditLine, RiStarSFill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CTable from "../../../components/Table/table";
import axiosInstance from "../../../helpers/api";
import { openCreateQuestionModel } from "../../../modules/admin-modules/models/Actions";
import {
  bookmarkQuestionBankRequest,
  deleteQuestionBankRequest,
} from "../../../modules/admin-modules/questions-bank/Actions";

const C = {
  primary: brandPalette.primary,
  secondary: brandPalette.accentDark,
  border: brandPalette.stone,
};

const iconBtnProps = {
  variant: "ghost",
  border: "0",
  size: "sm",
  color: C.primary,
  _hover: { bg: "rgba(91, 13, 22, 0.08)", color: C.secondary },
};

const stripHtml = (html) =>
  (html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().toLowerCase();

const difficultyLabels = {
  1: "Notes",
  2: "School book",
  3: "Hard",
  4: "Past exams",
  5: "Revision",
};

const QuestionsBankTable = ({ setAdd, setSelectedData, searchTerm = "" }) => {
  const dispatch = useDispatch();
  const questionsBank = useSelector((state) => state.questionsBank.questionsBank);
  const isLoading = useSelector((state) => state.questionsBank.isLoading);
  const bookmarkLoading = useSelector((state) => state.questionsBank.bookmarkLoading);
  const suspendLoading = useSelector((state) => state.questionsBank.suspendLoading);
  const count = useSelector((state) => state.questionsBank.count);

  const [checkedItems, setCheckedItems] = useState([]);
  const [checkedIds, setCheckedIds] = useState([]);

  const [offset, setPage] = useState(0);
  const [size, setPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(0);
  const [selected, setSelected] = useState({ id: "" });

  const filteredList = useMemo(() => {
    const raw = Array.isArray(questionsBank) ? questionsBank : [];
    const q = (searchTerm || "").trim().toLowerCase();
    if (!q) return raw;
    return raw.filter((row) => stripHtml(row?.question).includes(q));
  }, [questionsBank, searchTerm]);

  useEffect(() => {
    setCheckedItems(new Array(filteredList.length).fill(false));
    setCheckedIds([]);
  }, [filteredList.length, questionsBank]);

  useEffect(() => {
    setPageNumber(0);
  }, [searchTerm]);

  const allChecked = filteredList.length > 0 && checkedItems.length === filteredList.length && checkedItems.every(Boolean);
  const isIndeterminate = checkedItems.some(Boolean) && !allChecked;

  const viewData = (data) => {
    setSelectedData(data);
    setSelected(data);
  };

  const Data = filteredList.map((data, index) => ({
    ...data,
    select: (
      <Checkbox
        isChecked={Boolean(checkedItems[index])}
        onChange={(e) => {
          const next = [...checkedItems];
          next[index] = e.target.checked;
          setCheckedItems(next);
          if (e.target.checked) {
            setCheckedIds((s) => [...s, data?.id]);
          } else {
            setCheckedIds((s) => s.filter((id) => id !== data?.id));
          }
        }}
      />
    ),
    defcultyLevels: <Text fontWeight="600" color={C.secondary}>{difficultyLabels?.[data?.difficulty_level] ?? "—"}</Text>,
    index: `#${index + 1}`,
    chapter_id: data?.chapter?.id,
    questionTable: (
      <Box maxW="320px" fontSize="sm" className="question-bank-html" dangerouslySetInnerHTML={{ __html: data?.question }} />
    ),
    edit: (
      <IconButton
        {...iconBtnProps}
        aria-label="Edit question"
        fontSize="20px"
        icon={<RiFileEditLine />}
        onClick={(e) => {
          e.stopPropagation();
          viewData(data);
          setAdd(false);
          dispatch(openCreateQuestionModel());
        }}
      />
    ),
    delete: (
      <Button
        size="sm"
        borderRadius="full"
        fontWeight="800"
        onClick={(e) => {
          e.stopPropagation();
          dispatch(deleteQuestionBankRequest(data?.id));
        }}
        isLoading={data?.id === selected.id ? suspendLoading : false}
        leftIcon={data?.deleted_at == null ? null : <RiCheckFill />}
        bg={data?.deleted_at == null ? C.primary : "red.300"}
        color={data?.deleted_at == null ? "white" : "black"}
      >
        {data?.deleted_at == null ? "Suspend" : "Suspended"}
      </Button>
    ),
    bookmark: (
      <IconButton
        aria-label="Bookmark"
        as={RiStarSFill}
        bg="transparent"
        _focus={{ outline: "none" }}
        onClick={(e) => {
          e.stopPropagation();
          dispatch(bookmarkQuestionBankRequest(data?.id));
        }}
        isLoading={data?.id === selected.id ? bookmarkLoading : false}
        color={data?.book_marked === 0 ? "gray.300" : "goldenrod"}
      />
    ),
  }));

  const checkAllQuestions = (val) => {
    if (val) {
      setCheckedItems(filteredList.map(() => true));
      setCheckedIds(filteredList.map((d) => d?.id));
    } else {
      setCheckedItems(new Array(filteredList.length).fill(false));
      setCheckedIds([]);
    }
  };

  const suspendAll = () => {
    const formData = new FormData();
    formData.append("_method", "PUT");
    checkedIds.forEach((val, idx) => formData.append(`question_ids[${idx}]`, val));
    try {
      axiosInstance.post("/admin/bank_questions/suspend", formData);
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const columns = [
    {
      Header: (
        <Checkbox
          isChecked={allChecked}
          isIndeterminate={isIndeterminate}
          onChange={(e) => checkAllQuestions(e.target.checked)}
        />
      ),
      accessor: "select",
    },
    { Header: "#", accessor: "index" },
    { Header: "Question", accessor: "questionTable" },
    { Header: "Difficulty", accessor: "defcultyLevels" },
    { Header: "Edit", accessor: "edit" },
    { Header: "Bookmark", accessor: "bookmark" },
    { Header: "Suspend", accessor: "delete" },
  ];

  const totalPage = Math.max(1, Math.ceil((filteredList.length || count || 0) / size));

  return (
    <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
      <Button
        display={checkedIds.length ? "inline-flex" : "none"}
        mb={4}
        borderRadius="full"
        bg={C.secondary}
        color="white"
        fontWeight="800"
        size="sm"
        _hover={{ bg: C.primary }}
        onClick={suspendAll}
      >
        Suspend selected ({checkedIds.length})
      </Button>
      <Box
        bg="var(--surface-raised)"
        border="1px solid"
        borderColor={C.border}
        borderRadius="16px"
        overflow="hidden"
        boxShadow="sm"
      >
        <CTable
          selectedData={viewData}
          Columns={columns}
          Data={Data}
          path="openModel"
          Title=""
          subTitle=""
          btnTitle=""
          placeHolder=""
          noSearchBar
          noFilter
          footerBtnTitle
          filterList
          addButton
          addButtonTitle=""
          setPage={setPage}
          setPerPage={setPerPage}
          currentpage={pageNumber}
          setPageNumber={setPageNumber}
          noSecondSearchBar
          isLoading={isLoading}
          perPage={size}
          totalPage={totalPage}
          wrapperProps={{ maxH: "unset", overflow: "auto" }}
          tableProps={{ bg: "transparent", borderRadius: "0", mb: 0 }}
          theadProps={{ bg: "var(--surface-muted)", color: "var(--text-primary)" }}
        />
      </Box>
    </Box>
  );
};

export default QuestionsBankTable;
